import { Maybe } from "@flockcover/flock-svc-core-types"
import { Badge, CellProps, Column, DivStyledAsLink, Link, TableWithLimitBar, Text } from "@shared/primitive-components"
import { Collection } from "@shared/types"
import { formatPercentage } from "@shared/utils/formatters/formatPercentage"
import { navigationWithAltClick } from "@shared/utils/helpers/navigationWithAltClick"
import { useCallback, useMemo } from "react"
import { UserWithLaptopIcon } from "@shared/primitive-components/src/Icons"
import { useAuth } from "../../../../contexts/AuthProvider"
import { CONNECTIVITY_TARGET } from "../../../../const/connectivity"
import { Policy } from "../../api/useSearchPolicies"
import * as styles from "./styles/policiesTableStyles"

const StatusCell = ({ value }: CellProps<string, Policy>) => (
  <Badge variant={value === `Active` ? `brand` : value === `Cancelled` || value === `Void` ? `error` : `default`}>
    {value}
  </Badge>
)

const ConnectivityPercentageCell = ({ value }: CellProps<Maybe<number>, Policy>) =>
  value !== null ? (
    <Text css={value >= CONNECTIVITY_TARGET ? styles.green : styles.red}>{formatPercentage(value)}</Text>
  ) : (
    <Text>-</Text>
  )

export type PoliciesTableProps = Readonly<{
  onLimitChange: (value: number) => void
  collection: Collection<Policy>
  loading?: boolean
  limit: number
}>

export const PoliciesTable = ({ collection, loading, limit, onLimitChange }: PoliciesTableProps) => {
  const auth = useAuth()
  const authType = auth.getAuthUser()?.type
  const isAdminOrCarrier = authType === `admin` || authType === `claims_admin` || authType === `carrier`
  const NumberCell = useCallback(
    ({ value, row }: CellProps<string, Policy>) => {
      if (row.original.nonQuoted && !row.original.meta?.inceptingVehicleSetId) {
        return value
      }

      if (isAdminOrCarrier && !row.original.nonQuoted) {
        return (
          <DivStyledAsLink onClick={(e) => navigationWithAltClick(`policies/${row.original.id}`, e)}>
            {value}
          </DivStyledAsLink>
        )
      }

      return (
        <Link href={`/policy/${row.original.id}/fleet`} target="_blank" rel="noopener noreferrer">
          {value}
        </Link>
      )
    },
    [isAdminOrCarrier]
  )
  const FleetPortalLinkCell = useCallback(
    ({ row }: CellProps<string, Policy>) => (
      <Link href={`/policy/${row.original.id}/fleet`} target="_blank" rel="noopener noreferrer">
        <UserWithLaptopIcon />
      </Link>
    ),
    []
  )

  const columns = useMemo<ReadonlyArray<Column<Policy>>>(
    () => [
      {
        Header: `Policy number`,
        accessor: `number`,
        Cell: NumberCell,
      },
      {
        Header: `Insured name`,
        accessor: `insuredName`,
      },
      {
        Header: `Date issued`,
        accessor: `issueDate`,
      },
      {
        Header: `Connectivity`,
        accessor: `connectivityRate`,
        Cell: ConnectivityPercentageCell,
      },
      {
        Header: `Premium`,
        accessor: `premium`,
      },
      {
        Header: `Status`,
        accessor: `status`,
        Cell: StatusCell,
      },
    ],
    [NumberCell]
  )
  const columnsForAdmins = useMemo<ReadonlyArray<Column<Policy>>>(() => {
    const copyColumns = [...columns]

    if (isAdminOrCarrier) {
      copyColumns.push({
        Header: `Fleet Portal`,
        Cell: FleetPortalLinkCell,
      })
    }

    return copyColumns
  }, [isAdminOrCarrier, columns, FleetPortalLinkCell])

  return (
    <TableWithLimitBar<Policy>
      data={collection.items}
      loading={loading}
      columns={columnsForAdmins}
      limitBarProps={{
        options: [10, 25, 100],
        total: collection.total,
        onChange: onLimitChange,
        value: limit,
      }}
      css={{
        header: styles.darkBoldHeader,
      }}
    />
  )
}
